import { Injectable } from '@angular/core';
import { HttpService } from '../../../core/http.service';
import { InterviewUrl } from './interview.url.service';
import { Observable, map, forkJoin, switchMap, of } from 'rxjs';
import { WordPressPost, WordPressComment, WordPressCategory, WordPressMedia } from '../../../DTO/wordpress/post.dto';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';


@Injectable({
  providedIn: 'root'
})
export class InterviewService {

  constructor(
    private httpService: HttpService,
    private interviewUrl: InterviewUrl,
    private http: HttpClient
  ) { }

  // Get specific post/interview by ID with full details
  getPostById(postId: number): Observable<WordPressPost> {
    const url = this.interviewUrl.getPostById(postId);
    return this.httpService.get<WordPressPost>(url).pipe(
      map(post => {
        // Format date and clean content
        post.date = this.formatDate(post.date);
        return post;
      })
    );
  }

  getPostSeoData(postId: number): Observable<any> {
    const url = this.interviewUrl.getPostById(postId);
    return this.httpService.get<any>(url);
  }

  // Get specific post/interview by slug with full details
  getPostBySlug(slug: string): Observable<WordPressPost> {
    const url = this.interviewUrl.getPostBySlug(slug);
    return this.httpService.get<WordPressPost[]>(url).pipe(
      map(posts => {
        if (posts.length === 0) {
          throw new Error(`Post with slug '${slug}' not found`);
        }
        const post = posts[0]; // WordPress API returns array for slug search
        // Format date and clean content
        post.date = this.formatDate(post.date);
        return post;
      })
    );
  }

  // Get comments for specific post
  getPostComments(postId: number): Observable<WordPressComment[]> {
    const url = this.interviewUrl.getPostComments(postId);
    return this.httpService.get<WordPressComment[]>(url).pipe(
      map(comments => {
        return comments.map(comment => {
          // Use date_gmt if available for more accurate time calculations
          if (comment.date_gmt) {
            comment.date = comment.date_gmt;
          }
          // Don't format the date here - let getRelativeTime handle it
          return comment;
        });
      })
    );
  }

  // Get paginated comments for specific post
  getPostCommentsWithPagination(postId: number, perPage: number = 5, page: number = 1): Observable<{comments: WordPressComment[], totalCount: number}> {
    const url = this.interviewUrl.getPostCommentsWithPagination(postId, perPage, page);
    return this.http.get<WordPressComment[]>(url, { observe: 'response' }).pipe(
      map(response => {
        const comments = response.body || [];
        const totalCount = parseInt(response.headers.get('X-WP-Total') || '0');

        return {
          comments: comments.map(comment => {
            // Use date_gmt if available for more accurate time calculations
            if (comment.date_gmt) {
              comment.date = comment.date_gmt;
            }
            // Don't format the date here - let getRelativeTime handle it
            return comment;
          }),
          totalCount: totalCount
        };
      })
    );
  }

  // Get all comments
  getAllComments(): Observable<WordPressComment[]> {
    const url = this.interviewUrl.getAllComments();
    return this.httpService.get<WordPressComment[]>(url).pipe(
      map(comments => {
        return comments.map(comment => {
          // Use date_gmt if available for more accurate time calculations
          if (comment.date_gmt) {
            comment.date = comment.date_gmt;
          }
          // Don't format the date here - let getRelativeTime handle it
          return comment;
        });
      })
    );
  }

  getPopularInterviews(limit = 3): Observable<WordPressPost[]> {
    const url = this.interviewUrl.getPopularPosts(limit);
    return this.httpService.get<WordPressPost[]>(url).pipe(
      map(posts => {
        return posts.map(post => {
          // Format date and clean content
          post.date = this.formatDate(post.date);
          if (post.excerpt?.rendered) {
            post.excerpt.rendered = this.stripHtml(post.excerpt.rendered);
          }
          return post;
        });
      })
    );
  }

  // Get latest interviews ordered by date (newest first)
  getLatestInterviews(limit = 3): Observable<WordPressPost[]> {
    const url = this.interviewUrl.getLatestPosts(limit);
    return this.httpService.get<WordPressPost[]>(url).pipe(
      map(posts => {
        return posts.map(post => {
          // Format date and clean content
          post.date = this.formatDate(post.date);
          if (post.excerpt?.rendered) {
            post.excerpt.rendered = this.stripHtml(post.excerpt.rendered);
          }
          return post;
        });
      })
    );
  }

  // Get recent interviews as fallback
  getRecentInterviews(limit = 3): Observable<WordPressPost[]> {
    const url = this.interviewUrl.getRecentPosts(limit);
    return this.httpService.get<WordPressPost[]>(url).pipe(
      map(posts => {
        return posts.map(post => {
          // Format date and clean content
          post.date = this.formatDate(post.date);
          if (post.excerpt?.rendered) {
            post.excerpt.rendered = this.stripHtml(post.excerpt.rendered);
          }
          return post;
        });
      })
    );
  }

  getCategoryName(categoryIds: number[], post?: WordPressPost): string {
    if (!categoryIds || categoryIds.length === 0) return 'Interview';

    // Try to get category name from embedded data first (most reliable)
    if (post && post._embedded && post._embedded['wp:term'] && post._embedded['wp:term'][0]) {
      const embeddedCategories = post._embedded['wp:term'][0];
      const category = embeddedCategories.find((cat: any) => categoryIds.includes(cat.id));
      if (category) {
        return category.name;
      }
    }

    // Fallback to default
    return 'Interview';
  }
  // Get related posts by category
  getRelatedPosts(categoryId: number, excludePostId: number, limit = 3): Observable<WordPressPost[]> {
    const url = this.interviewUrl.getRelatedPosts(categoryId, excludePostId, limit);
    return this.httpService.get<WordPressPost[]>(url).pipe(
      map(posts => {
        return posts.map(post => {
          post.date = this.formatDate(post.date);
          return post;
        });
      })
    );
  }

  // Get post with complete details (post + comments + related posts)
  getPostWithFullDetails(postId: number): Observable<{
    post: WordPressPost;
    comments: WordPressComment[];
    relatedPosts: WordPressPost[];
  }> {
    return this.getPostById(postId).pipe(
      switchMap(post => {
        // Get comments for this specific post and related posts
        const comments$ = this.getPostComments(postId);
        const relatedPosts$ = post.categories.length > 0
          ? this.getRelatedPosts(post.categories[0], postId)
          : of([]);

        return forkJoin({
          post: of(post),
          comments: comments$,
          relatedPosts: relatedPosts$
        });
      })
    );
  }

  // Get featured image with fallback
  getPostFeaturedImage(post: WordPressPost): string {
    // First, try to get the image from embedded data
    if (post._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
      return post._embedded['wp:featuredmedia'][0].source_url;
    }

    // Fallback to featured_media_url if available
    if (post.featured_media_url) {
      return post.featured_media_url;
    }

    // Return empty string if no image is available
    return 'images/graphics3.svg';
  }

  // Get post excerpt with word limit
  getPostExcerpt(post: WordPressPost, wordLimit = 30): string {
    let excerpt = post.excerpt?.rendered || post.content?.rendered || '';
    excerpt = this.stripHtml(excerpt);
    const words = excerpt.split(' ');
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(' ') + '...';
    }
    return excerpt;
  }

  // Format reading time estimation
  getReadingTime(content: string): number {
    const wordsPerMinute = 200;
    const words = this.stripHtml(content).split(' ').length;
    return Math.ceil(words / wordsPerMinute);
  }

  // Get categories for the post
  getCategoryNames(categoryIds: number[], categories: WordPressCategory[] = [], post?: WordPressPost): string[] {
    if (!categoryIds || categoryIds.length === 0) return ['Interview'];

    // First try to get category names from embedded data if post is provided
    if (post && post._embedded && post._embedded['wp:term']) {
      const embeddedCategories = post._embedded['wp:term'][0] || [];
      const categoryNames: string[] = [];

      for (const categoryId of categoryIds) {
        const embeddedCategory = embeddedCategories.find((cat: any) => cat.id === categoryId);
        if (embeddedCategory) {
          categoryNames.push(embeddedCategory.name);
        } else {
          // Fallback to provided categories array
          const category = categories.find(cat => cat.id === categoryId);
          categoryNames.push(category ? category.name : 'Interview');
        }
      }

      return categoryNames.length > 0 ? categoryNames : ['Interview'];
    }

    // Fallback to provided categories array
    return categoryIds.map(id => {
      const category = categories.find(cat => cat.id === id);
      return category ? category.name : 'Interview';
    });
  }

  // Format comment count
  getCommentCount(comments: WordPressComment[]): string {
    const count = comments.length;
    if (count === 0) return 'No comments';
    if (count === 1) return '1 comment';
    return `${count} comments`;
  }

  // Utility functions
  private formatDate(dateString: string): string {
    console.log('formatDate called with:', dateString);

    let date: Date;
    try {
      // If the date string is already in ISO format (contains 'T'), parse it directly
      if (dateString && dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        // For other formats, add UTC timezone if not present
        if (dateString && !dateString.includes('Z')) {
          date = new Date(dateString + 'Z');
        } else {
          date = new Date(dateString);
        }
      }
    } catch (error) {
      console.error('Error parsing date in formatDate:', dateString, error);
      return new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date in formatDate:', dateString);
      return new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    }

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    };
    const result = date.toLocaleDateString('en-GB', options);
    console.log('formatDate result:', result);
    return result;
  }

  private stripHtml(html: string): string {
    // Use regex to strip HTML tags for SSR compatibility
    let cleanText = html.replace(/<[^>]*>/g, '');

    cleanText = cleanText
      .replace(/&amp;#8220;/g, '"')
      .replace(/&amp;#8221;/g, '"')
      .replace(/&amp;#8216;/g, "'")
      .replace(/&amp;#8217;/g, "'")
      .replace(/&amp;#8211;/g, '-')
      .replace(/&amp;#8212;/g, '--')
      .replace(/&amp;#8230;/g, '...')
      .replace(/&amp;hellip;/g, '...')
      .replace(/&amp;#038;/g, '&')
      .replace(/&amp;/g, '&');

    cleanText = cleanText
      .replace(/&#8220;/g, '"')
      .replace(/&#8221;/g, '"')
      .replace(/&#8216;/g, "'")
      .replace(/&#8217;/g, "'")
      .replace(/&#8211;/g, '-')
      .replace(/&#8212;/g, '--')
      .replace(/&#8230;/g, '...')
      .replace(/&hellip;/g, '...')
      .replace(/&#038;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&nbsp;/g, ' ');

    cleanText = cleanText.replace(/\u00A0/g, ' ').replace(/\u2026/g, '...');

    return cleanText.replace(/\s+/g, ' ').trim();
  }

          // Format relative time (e.g., "2 hours ago")
  getRelativeTime(dateString: string): string {
    const now = new Date();

    // Debug: Log the incoming date string
    console.log('getRelativeTime called with dateString:', dateString);

    // Handle different date formats and ensure proper timezone handling
    let date: Date;
    try {
      // If the date string is in ISO format (contains 'T'), parse it directly
      if (dateString && dateString.includes('T')) {
        // The date from the API is now in UTC format (date_gmt)
        // Parse it as UTC time
        date = new Date(dateString + 'Z');
      } else {
        // For other formats, assume it's already formatted by formatDate method
        // Try to parse it as a formatted date string
        date = new Date(dateString);
      }
    } catch (error) {
      console.error('Error parsing date in getRelativeTime:', dateString, error);
      return 'Just now';
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date in getRelativeTime:', dateString);
      return 'Just now';
    }

    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Debug: Log the calculated difference
    console.log('Date difference in seconds:', diffInSeconds, 'Now:', now, 'Comment date:', date);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;

    return `${Math.floor(diffInSeconds / 31536000)} years ago`;
  }

  // Submit a new comment
  submitComment(postId: number, commentData: any): Observable<WordPressComment> {
    const url = this.interviewUrl.submitComment(postId);
    const payload = {
      ...commentData,
      post: postId
    };


    return this.httpService.post<WordPressComment>(url, payload).pipe(
      map(comment => {
        // Format comment date
        comment.date = this.formatDate(comment.date);
        return comment;
      })
    );
  }

  // Submit a rating (implemented as a special type of comment)
  submitRating(postId: number, ratingData: any): Observable<WordPressComment> {
    const url = this.interviewUrl.submitComment(postId);

    // Create a cleaner content format for ratings
    const stars = '★'.repeat(ratingData.rating) + '☆'.repeat(5 - ratingData.rating);
    const content = `Rating: ${stars} (${ratingData.rating}/5)${ratingData.feedback ? ` - ${ratingData.feedback}` : ''}`;

    // Create WordPress-friendly payload
    const payload = {
      post: postId,
      author_name: ratingData.author_name || 'Anonymous',
      author_email: ratingData.author_email || 'anonymous@example.com',
      content: content,
      // WordPress comment meta fields as array
      meta: [
        {
          key: 'rating_value',
          value: ratingData.rating.toString()
        },
        {
          key: 'comment_type',
          value: 'rating'
        },
        {
          key: 'rating_feedback',
          value: ratingData.feedback || ''
        }
      ]
    };



    return this.httpService.post<WordPressComment>(url, payload).pipe(
      map(comment => {
        // Format comment date
        comment.date = this.formatDate(comment.date);
        return comment;
      })
    );
  }

  // Get comment avatar URL
  getCommentAvatarUrl(comment: WordPressComment, size: string = '48'): string {
    if (comment.author_avatar_urls && comment.author_avatar_urls[size]) {
      return comment.author_avatar_urls[size];
    }
    // Return default avatar if no avatar URLs available
    return 'https://secure.gravatar.com/avatar/00000000000000000000000000000000?d=mm&f=y';
  }

  // Check if comment is a rating
  isRatingComment(comment: WordPressComment): boolean {
    return comment.meta?.some((meta: any) =>
      meta.key === 'comment_type' && meta.value === 'rating'
    ) || comment.content.rendered.includes('rating-comment');
  }

  // Extract rating value from comment
  getRatingValue(comment: WordPressComment): number {
    if (!this.isRatingComment(comment)) return 0;

    const metaRating = comment.meta?.find((meta: any) => meta.key === 'rating_value');
    if (metaRating) {
      return parseInt(metaRating.value, 10);
    }

    // Fallback: count stars in content
    const starCount = (comment.content.rendered.match(/★/g) || []).length;
    return starCount;
  }

  getCategoryIdBySlug(slug: string): Observable<number | null> {
    const url = this.interviewUrl.getCategoryIdBySlug(slug);
    return this.httpService.get<any[]>(url)
      .pipe(map(categories => categories.length ? categories[0].id : null));
  }

  getPostsByCategoryId(categoryId: number): Observable<any[]> {
    const url = this.interviewUrl.getPostsByCategoryId(categoryId);
    return this.httpService.get<any[]>(url);
  }

  getPostsByCategoryIdWithPagination(categoryId: number, perPage: number = 6, page: number = 1): Observable<any[]> {
    const url = this.interviewUrl.getPostsByCategoryIdWithPagination(categoryId, perPage, page);
    return this.httpService.get<any[]>(url);
  }

  // Get all posts (for "View All" functionality)
  getAllPosts(): Observable<any[]> {
    const url = this.interviewUrl.getAllPosts();
    return this.httpService.get<any[]>(url);
  }

  // Get all posts with pagination
  getAllPostsWithPagination(perPage: number = 6, page: number = 1): Observable<any[]> {
    const url = this.interviewUrl.getAllPostsWithPagination(perPage, page);
    return this.httpService.get<any[]>(url);
  }

  // Get AddToAny share buttons HTML
  getShareButtons(): Observable<{ html: string }> {
    const url = this.interviewUrl.getShareButtons();
    return this.httpService.get<{ html: string }>(url);
  }

  // Get all categories
  getCategories(): Observable<any[]> {
    const url = this.interviewUrl.getCategories();
    return this.httpService.get<any[]>(url);
  }

  // Get all share counts from API
  getAllShareCounts(): Observable<any> {
    const url = this.interviewUrl.getAllShareCounts();
    return this.httpService.get<any>(url);
  }

  // getCommentLikeCount() {
  //   const url = this.interviewUrl.getLikeCount();
  //   return this.httpService.get<{ post_id: number, like_count: number }>(url);
  // }

  // Get like count for a specific comment
  // getCommentLikeCountById() {
  //   const url = this.interviewUrl.getCommentLikeCount();
  //   return this.httpService.get<{ comment_id: number, like_count: number }>(url);
  // }

  likeComment(commentId: number) {
    const url = this.interviewUrl.likeComment(commentId);
    return this.httpService.post<{ comment_id: number, like_count: number, liked: boolean }>(url, {});
  }

  getCommentLikesByPostId(postId: number) {
    const url = this.interviewUrl.getCommentLikesByPostId(postId);
    return this.httpService.get<Array<{ comment_id: string, author: string, content: string, like_count: number }>>(url);
  }
  getPostViewCount(postId: number): Observable<{post_id: number, views: number}> {
    const url = this.interviewUrl.getPostViewCount(postId);
    return this.httpService.get<{post_id: number, views: number}>(url);
  }

  /**
   * Increment post view count using POST request (first visit only)
   */
  incrementPostViews(postId: number): Observable<any> {
    const url = `${environment.youtubeApiUrl}post-view`;
    const body = { post_id: postId };

    // Use proper JSON headers for WordPress REST API
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    console.log('InterviewService: Making POST request to increment view count:', url);
    console.log('POST body:', body);
    console.log('Headers:', headers);

    return this.http.post(url, body, { headers });
  }

  /**
   * Get current view count from the custom API using GET request
   * WordPress handles view increment automatically via footer script
   */
  getViewCountFromApi(postId: number): Observable<any> {
    // Use GET method to fetch current view count only
    const url = `${environment.youtubeApiUrl}post-view?post_id=${postId}`;
    console.log('InterviewService: Making GET request to fetch current view count:', url);
    return this.http.get(url);
  }

  submitCommentToApi(postId: number, data: any): Observable<any> {
    // Optionally add user_agent if available
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      data.user_agent = navigator.userAgent;
    }

    // Build API URL with post_id parameter
    const apiUrl = `${environment.youtubeApiUrl}submit-comment?post_id=${postId}`;

    return this.http.post(apiUrl, data).pipe(
      map((comment: any) => {
        // Debug: Log the raw comment from API
        console.log('Raw comment from API:', comment);

        // Use date_gmt if available for more accurate time calculations
        if (comment.date_gmt) {
          comment.date = comment.date_gmt;
          console.log('Using date_gmt for comment:', comment.date_gmt);
        }

        // Don't format the date here - let getRelativeTime handle the original date
        // This ensures consistent time calculations
        console.log('Final comment date for display:', comment.date);
        return comment;
      })
    );
  }

  // Submit rating to the new API endpoint
  submitRatingToApi(ratingData: any): Observable<any> {
    const url = `${environment.youtubeApiUrl}rate-post`;
    return this.httpService.post(url, ratingData);
  }

  // Get banner ads
  getBannerAds(): Observable<any> {
    const url = this.interviewUrl.getBannerAds();
    return this.httpService.get<any>(url);
  }

  
}
